<template>
  <div>
    <section v-if="activeScreen === ActiveScreens.Login">
      <h1 class="title">Sign In</h1>

      <b-field label="Email">
        <b-input
          type="email"
          v-model="email"
          placeholder="Your email"
          key="user-email"
          required
          size="is-medium">
        </b-input>
      </b-field>

      <b-field label="Password">
        <b-input
          type="password"
          key="user-password"
          v-model="password"
          password-reveal
          placeholder="Your password"
          required
          size="is-medium"
          @keyup.native.enter="onLoginClick"
        >
        </b-input>
      </b-field>

      <div class="buttons">
        <button class="button is-success" @click="onLoginClick">Sign In</button>
        <button class="button" @click="onForgotPassword">Forgot Password?</button>
      </div>

      <b-notification type="is-success" v-if="passwordChangeSuccessMsg !== null">
        {{passwordChangeSuccessMsg}}
      </b-notification>

      <b-notification type="is-danger"
        v-if="exception === AwsExceptions.UserNotFoundException"
      >
        UserNotFoundException
      </b-notification>

      <b-notification type="is-warning" v-if="validationError !== null">
        <span v-if="validationError === ValidationErrors.EmailAndPwRequired">
          Email and Password are required
        </span>
        <span v-if="validationError === ValidationErrors.EmailRequired">
          Email is required
        </span>
      </b-notification>

      <b-notification type="is-warning" v-if="codeDeliveryDestinationMsg !== null">
        {{codeDeliveryDestinationMsg}}
      </b-notification>

      <!--b-checkbox>Remember me</b-checkbox-->

      <b-notification type="is-danger" v-if="unhandledExceptions !== null">
        {{unhandledExceptions}}
      </b-notification>

      <hr />
      <button class="button is-primary" @click="$router.push('/register')">No Account? Sign Up</button>
    </section>

    <section v-if="activeScreen === ActiveScreens.RequireNewPassword">
      <h1 class="title">New Password Required</h1>

      <b-field label="Please Enter a New Password"
        :message="[
          'Password must have at least 8 characters',
          'Password must contain an uppercase letter',
          'Password must contain a lowercase letter',
          'Password must contain a number',
          'Password must contain a special character',
        ]"
      >
        <b-input
          type="password"
          key="user-new-password"
          name="new-password"
          v-validate="'required|min:8|verify_password'"
          v-model="newPassword"
          password-reveal
          placeholder="Your password"
          required
          size="is-medium">
        </b-input>
      </b-field>
      <b-notification type="is-warning" v-show="errors.has('new-password')">
        {{ errors.first('new-password') }}
      </b-notification>

      <div class="buttons">
        <button class="button is-primary" @click="onSaveNewPassword">Save Password</button>
      </div>

      <b-notification type="is-danger" v-if="unhandledExceptions !== null">
        {{unhandledExceptions}}
      </b-notification>
    </section>

    <section v-if="activeScreen === ActiveScreens.RequireNewPasswordWithVerification">
      <h1 class="title">Choose a New Password</h1>

      <b-notification type="is-warning" v-if="codeDeliveryDestinationMsg !== null">
        {{codeDeliveryDestinationMsg}}
      </b-notification>

      <b-field label="Verification Code"
        message="[ 'Please check your e-mail for',
          'the verification code from',
          'no-reply@verificationemail.com' ].join('')"
      >
        <b-input
          type="text"
          key="user-verification-code"
          v-model="verificationCode"
          placeholder="Verification code"
          size="is-medium"
        >
        </b-input>
      </b-field>

      <b-field label="Choose a new password" message="Please enter a new password">
        <b-input
          @input="handlePassword"
          type="password"
          name="new-password"
          v-validate="'required|min:8|verify_password'"
          v-model="newPassword"
          password-reveal
          placeholder="Your password"
          key="user-new-password"
          required
          size="is-medium">
        </b-input>
      </b-field>
      <b-notification type="is-warning" v-show="errors.has('new-password')">
        {{ errors.first('new-password') }}
      </b-notification>

      <b-notification type="is-danger" v-if="unhandledExceptions !== null">
        {{unhandledExceptions}}
      </b-notification>

      <div class="buttons">
        <button class="button is-primary"
                :disabled="errors.has('new-password') || !verificationCode || !newPassword"
                @click="onSaveResetPassword">Save Password</button>
        <button class="button" disabled>Re-send Code</button>
      </div>
    </section>
  </div>
</template>

<script>
import {
  CognitoUserPool, CognitoUser, AuthenticationDetails,
} from 'amazon-cognito-identity-js';
import { mapMutations } from 'vuex';
import ApiUtil from '../utils/ApiUtil';

const USER_POOL_ID = 'us-east-1_W19nxF3iD';
const CLIENT_ID = '2cndfls54vvsqeiv1gccdkmbqn';

export default {
  name: 'auth-modal',
  data() {
    return {
      AwsExceptions: {
        PasswordResetRequiredException: 'PasswordResetRequiredException',
        UserNotFoundException: 'UserNotFoundException',
        LimitExceededException: 'LimitExceededException',
      },
      ValidationErrors: {
        EmailAndPwRequired: 'EmailAndPwRequired',
        EmailRequired: 'EmailRequired',
      },
      ActiveScreens: {
        Login: 'Login',
        RequireNewPasswordWithVerification: 'RequireNewPasswordWithVerification',
        RequireNewPassword: 'RequireNewPassword',
      },
      userPool: new CognitoUserPool({
        UserPoolId: USER_POOL_ID,
        ClientId: CLIENT_ID,
      }),
      password: null,
      newPassword: null,
      confirmNewPassword: null,
      email: null,
      verificationCode: null,
      exception: null,
      validationError: null,
      cognitoUser: null,
      codeDeliveryDestinationMsg: null,
      passwordChangeSuccessMsg: null,
      unhandledExceptions: null,
      activeScreen: 'Login',
    };
  },
  mounted() {
    this.setIsLoading(false);
  },
  methods: {
    ...mapMutations([
      'setCookie',
      'setUserContext',
      'setIsLoading',
    ]),
    onLoginClick() {
      // https://docs.aws.amazon.com/cognito/latest/developerguide/using-amazon-cognito-user-identity-pools-javascript-examples.html

      if (!this.email || !this.password) {
        this.validationError = this.ValidationErrors.EmailAndPwRequired;
        return;
      }
      this.validationError = null;

      const lowerCasedEmail = this.email.toLowerCase();
      const authenticationDetails = new AuthenticationDetails({
        Username: lowerCasedEmail,
        Password: this.password,
      });
      const userData = {
        Username: lowerCasedEmail,
        Pool: this.userPool,
      };

      this.cognitoUser = new CognitoUser(userData);

      this.setIsLoading(true);
      this.cognitoUser.authenticateUser(authenticationDetails, {
        onSuccess: async (result) => {
          this.setIsLoading(false);
          this.setCookie(result.idToken.jwtToken);
          const userInfo = await ApiUtil.getUserInfo();
          this.setUserContext(userInfo);
          this.$router.push({ name: 'missions' });
        },
        onFailure: (err) => {
          this.setIsLoading(false);
          if (this.AwsExceptions.hasOwnProperty(err.code)) {
            console.log('this is a handled exception: ', err.code);
            this.exception = err.code;
            if (err.code === this.AwsExceptions.PasswordResetRequiredException) {
              this.activeScreen = this.ActiveScreens.RequireNewPasswordWithVerification;
              this.cognitoUser.forgotPassword({
                onSuccess: () => {
                },
                onFailure: (fpErr) => {
                  this.unhandledExceptions = fpErr.message;
                },
                inputVerificationCode: (data) => {
                  this.codeDeliveryDestinationMsg = `Your verification code was sent to ${data.CodeDeliveryDetails.Destination}`;
                },
              });
            }
          } else {
            this.unhandledExceptions = err;
          }
        },
        newPasswordRequired: (userAttributes, requiredAttributes) => {
          this.setIsLoading(false);
          this.activeScreen = this.ActiveScreens.RequireNewPassword;
        },
      });
    },
    onSaveNewPassword() {
      this.cognitoUser.completeNewPasswordChallenge(this.newPassword, [], {
        onSuccess: (result) => {
          this.setCookie(result.idToken.jwtToken);
          this.$router.push('/dashboard');
        },
        onFailure: (err) => {
          this.unhandledExceptions = err;
        },
      });
    },
    onSaveResetPassword() {
      this.cognitoUser.confirmPassword(this.verificationCode, this.newPassword, {
        onSuccess: () => {
          this.activeScreen = this.ActiveScreens.Login;
          this.passwordChangeSuccessMsg = 'Password Successfully Changed. Please Login.';
          this.codeDeliveryDestinationMsg = null;
        },
        onFailure: (err) => {
          this.unhandledExceptions = err;
        },
      });
    },
    onForgotPassword() {
      if (!this.email) {
        this.validationError = this.ValidationErrors.EmailRequired;
        return;
      }
      this.validationError = null;

      const userData = {
        Username: this.email,
        Pool: this.userPool,
      };
      this.cognitoUser = new CognitoUser(userData);

      this.activeScreen = this.ActiveScreens.RequireNewPasswordWithVerification;
      this.codeDeliveryDestinationMsg = 'Your verification code was sent to';
      this.cognitoUser.forgotPassword({
        onSuccess: () => {
        },
        onFailure: (fpErr) => {
          this.unhandledExceptions = fpErr.message;
        },
        inputVerificationCode: (data) => {
          this.activeScreen = this.ActiveScreens.RequireNewPasswordWithVerification;
          this.codeDeliveryDestinationMsg = `Your verification code was sent to ${data.CodeDeliveryDetails.Destination}`;
        },
      });
    },
  },
};
</script>
